import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { SimulationChangeType, FundSumDropMap, PolicyFundAdditions } from "../../../interfaces/DTO/simulation";
import { formatProductType } from "../../../utils/enumFormatter";
import { formatHandlerCode, formatPercentage } from "../../../utils/namesFormater";


export interface ChangeItem {
  policy_number: string;
  product_type: string;
  funds_sum: number;
  contact_id?: string;
  original_handler_code?: string;
  modified_handler_code?: string;
  original_saving_fee?: number;
  modified_saving_fee?: number;
  original_funds?: any[];
  modified_funds?: any[];
  original_funds_display?: string;
  modified_funds_display?: string;
  changes: Change[];
  assignment: string;
  fund_addition: PolicyFundAdditions | null;
  note?: string;
  change_types?: SimulationChangeType[];
  has_new_amount?: boolean;
  saving_fee_trend?: 'up' | 'down' | 'same';
}

export interface Change {
  type: 'new' | 'handler' | 'fee' | 'funds' | 'new_recurrent_deposit' | 'change_recurrent_deposit' | 'other';
  description?: string;
  from?: string;
  to?: string;
  color?: string;
}

const COLUMN_TITLES: Record<string, string> = {
  none: "ללא מינוי",
  full: "מינוי סוכן מלא",
  viewOnly: "מינוי לתצוגה בלבד",
};

export class ChangesDataUtils {
  /**
   * מחשב את סך כל התוספות לפוליסה (deposit + recurrent + transfer)
   */
  static getTotalFundAddition(additions: PolicyFundAdditions | undefined): number {
    if (!additions) return 0;
    const recurrent = additions.recurrent === null ? 0 : (additions.recurrent ?? 0);
    return (additions.deposit ?? 0) + recurrent + (additions.transfer ?? 0) - (additions.withdrawal ?? 0);
  }

  /**
   * מחשב רק deposit ו-transfer (ללא recurrent)
   * משמש כאשר recurrent כבר מעודכן ב-row.recurrent_deposit
   */
  static getNonRecurrentAdditions(additions: PolicyFundAdditions | undefined): number {
    if (!additions) return 0;
    return (additions.deposit ?? 0) + (additions.transfer ?? 0);
  }

  /**
   * מחשב את ההפרש בהוראת קבע (התוספת החדשה בלבד)
   */
  static getRecurrentDelta(originalRecurrent: number | undefined, modifiedRecurrent: number | undefined): number {
    const orig = originalRecurrent ?? 0;
    const mod = modifiedRecurrent ?? 0;
    return Math.max(0, mod - orig); // רק אם יש עלייה
  }

  /**
   * מחשב את נתוני השינויים עבור כל הפוליסות
   */
  static calculateChanges(
    originalRows: MimshakData[],
    modifiedRows: MimshakData[],
    assignmentMap: Record<string, "none" | "full" | "viewOnly"> = {},
    fundSumDropMap: FundSumDropMap = {}
  ): ChangeItem[] {
    return modifiedRows.map((modified) => {
      const original = originalRows.find((r) => r.policy_number === modified.policy_number);
      
      const changes: Change[] = [];
      let savingFeeTrend: 'up' | 'down' | 'same' = 'same';
      
      if (!original) {
        changes.push({
          type: 'new',
          description: 'פוליסה חדשה',
          color: '#28a745'
        });
      } else {
        // בדיקת שינוי יצרן
        if (original.handler_code !== modified.handler_code) {
          changes.push({
            type: 'handler',
            from: formatHandlerCode(original.handler_code),
            to: formatHandlerCode(modified.handler_code),
            color: '#007bff'
          });
        }

        // בדיקת שינוי עמלות עם צבע דינמי
        if (original.saving_fee !== modified.saving_fee) {
          const isImprovement = modified.saving_fee < original.saving_fee;
          savingFeeTrend = isImprovement ? 'down' : 'up';
          
          changes.push({
            type: 'fee',
            from: formatPercentage(original.saving_fee),
            to: formatPercentage(modified.saving_fee),
            color: isImprovement ? '#28a745' : '#dc3545' // ירוק אם ירדו, אדום אם עלו
          });
        }

        // בדיקת שינוי קרנות
        const { hasChanges, originalFundsDisplay, modifiedFundsDisplay } = this.analyzeFundChanges(original, modified);
        if (hasChanges) {
          changes.push({
            type: 'funds',
            from: originalFundsDisplay,
            to: modifiedFundsDisplay,
            color: '#6f42c1'
          });
        }

        // בדיקת שינוי הוראת קבע חודשית
        const originalRecurrent = original.recurrent_deposit ?? 0;
        const modifiedRecurrent = modified.recurrent_deposit ?? 0;
        if (originalRecurrent !== modifiedRecurrent) {
          if (originalRecurrent === 0 && modifiedRecurrent > 0) {
            // הוראת קבע חדשה
            changes.push({
              type: 'new_recurrent_deposit',
              from: `₪${originalRecurrent.toLocaleString()}`,
              to: `₪${modifiedRecurrent.toLocaleString()}`,
              color: '#17a2b8' // צבע תכלת
            });
          } else {
            // שינוי בהוראת קבע קיימת
            changes.push({
              type: 'change_recurrent_deposit',
              from: `₪${originalRecurrent.toLocaleString()}`,
              to: `₪${modifiedRecurrent.toLocaleString()}`,
              color: '#20c997' // ירוק-תכלת
            });
          }
        }
      }

      // חישוב תצוגת קרנות
      const { originalFundsDisplay, modifiedFundsDisplay } = this.analyzeFundChanges(original, modified);

      return {
        policy_number: modified.policy_number,
        product_type: formatProductType(modified.product_type),
        funds_sum: modified.funds_sum || 0,
        contact_id: modified.contact_id !== undefined && modified.contact_id !== null ? String(modified.contact_id) : undefined,
        original_handler_code: original?.handler_code,
        modified_handler_code: modified.handler_code,
        original_saving_fee: original?.saving_fee,
        modified_saving_fee: modified.saving_fee,
        original_funds_display: originalFundsDisplay,
        modified_funds_display: modifiedFundsDisplay,
        changes,
        assignment: COLUMN_TITLES[assignmentMap[modified.policy_number] || "none"],
        fund_addition: fundSumDropMap[modified.policy_number] || null,
        note: "",
        change_types: [] as SimulationChangeType[],
        has_new_amount: this.getTotalFundAddition(fundSumDropMap[modified.policy_number]) > 0,
        saving_fee_trend: savingFeeTrend
      };
    });
  }

  /**
   * ניתוח שינויים בקרנות ויצירת תצוגה מתאימה
   */
  private static analyzeFundChanges(original: MimshakData | undefined, modified: MimshakData): {
    hasChanges: boolean;
    originalFundsDisplay: string;
    modifiedFundsDisplay: string;
  } {
    if (!original) {
      // פוליסה חדשה - נציג רק את הקרנות החדשות
      const modifiedFunds = this.extractFundsFromProduct(modified);
      return {
        hasChanges: false,
        originalFundsDisplay: '',
        modifiedFundsDisplay: modifiedFunds.join(', ')
      };
    }

    const originalFunds = this.extractFundsFromProduct(original);
    const modifiedFunds = this.extractFundsFromProduct(modified);

    // בדיקה האם יש שינוי בקרנות
    const hasChanges = !this.arraysEqual(originalFunds, modifiedFunds);

    return {
      hasChanges,
      originalFundsDisplay: originalFunds.join(', '),
      modifiedFundsDisplay: modifiedFunds.join(', ')
    };
  }

  /**
   * חילוץ שמות קרנות מנתוני המוצר
   */
  private static extractFundsFromProduct(data: MimshakData): string[] {
    try {
      // התאמה ללוגיקה הקיימת שלך מ-SummaryChangesTable
      const pathCodes = data.data?.products?.[0] ? 
        [...new Set(this.getPathCodesFromProduct(data.data.products[0]))] : [];
      
      // כאן תצטרכי להתאים לפונקציה שלך שמחזירה את שמות הקרנות
      // לעת עתה אני אחזיר את הקודים בלבד
      return pathCodes.map(code => `קרן ${code}`);
    } catch (e) {
      return [];
    }
  }

  /**
   * חילוץ קודי מסלולים מנתוני המוצר (התאמה ללוגיקה שלך)
   */
  private static getPathCodesFromProduct(product: any): string[] {
    // כאן תצטרכי להתאים לפונקציה MimshakProductUtil.getPathCodesFromProduct
    // לעת עתה זה דוגמה
    if (!product || !product.paths) return [];
    return product.paths.map((path: any) => path.code || path.path_code).filter(Boolean);
  }

  /**
   * השוואת מערכים
   */
  private static arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, index) => val === sortedB[index]);
  }

  /**
   * בדיקה האם יש שינויים בקרנות (פונקציה מעודכנת)
   */
  private static hasFundChanges(original: MimshakData, modified: MimshakData): boolean {
    const { hasChanges } = this.analyzeFundChanges(original, modified);
    return hasChanges;
  }

  /**
   * חישוב סיכומים
   */
  static calculateSummary(changes: ChangeItem[]): {
    totalPolicies: number;
    totalAmount: number;
    totalAdditions: number;
    changesCount: number;
    feeReductions: number;
    totalFeeSavings: number;
    fundChanges: number;
  } {
    const feeReductions = changes.filter(item => item.saving_fee_trend === 'down').length;
    
    // חישוב חיסכון כולל בעמלות (באחוזים - לצורך הדוגמה)
    const totalFeeSavings = changes.reduce((sum, item) => {
      if (item.original_saving_fee && item.modified_saving_fee && item.saving_fee_trend === 'down') {
        return sum + (item.original_saving_fee - item.modified_saving_fee);
      }
      return sum;
    }, 0);

    const fundChanges = changes.filter(item => 
      item.original_funds_display && item.modified_funds_display && 
      item.original_funds_display !== item.modified_funds_display
    ).length;

    return {
      totalPolicies: changes.length,
      totalAmount: changes.reduce((sum, item) => sum + item.funds_sum, 0),
      totalAdditions: changes.reduce((sum, item) => sum + this.getTotalFundAddition(item.fund_addition), 0),
      changesCount: changes.filter(item => item.changes.length > 0).length,
      feeReductions,
      totalFeeSavings,
      fundChanges
    };
  }

  /**
   * פילטר לפי סוג שינוי
   */
  static filterByChangeType(changes: ChangeItem[], changeType: Change['type']): ChangeItem[] {
    return changes.filter(item => item.changes.some(change => change.type === changeType));
  }

  /**
   * מיון לפי סכום/פוליסה/שינויים
   */
  static sortChanges(changes: ChangeItem[], sortBy: 'amount' | 'policy' | 'changes'): ChangeItem[] {
    return [...changes].sort((a, b) => {
      switch (sortBy) {
        case 'amount':
          return b.funds_sum - a.funds_sum;
        case 'policy':
          return a.policy_number.localeCompare(b.policy_number);
        case 'changes':
          return b.changes.length - a.changes.length;
        default:
          return 0;
      }
    });
  }

  /**
   * יצירת טקסט עבור שינוי
   */
  static formatChangeText(change: Change): string {
    switch (change.type) {
      case 'new':
        return change.description || 'פוליסה חדשה';
      case 'handler':
        return `יצרן: ${change.from} ← ${change.to}`;
      case 'fee':
        return `דמי ניהול: ${change.from} ← ${change.to}`;
      case 'funds':
        return `מסלול: ${change.from} ← ${change.to}`;
      case 'new_recurrent_deposit':
        return `הוראת קבע חדשה: ${change.to}`;
      case 'change_recurrent_deposit':
        return `שינוי הוראת קבע: ${change.from} ← ${change.to}`;
      default:
        return change.description || '';
    }
  }

  /**
   * יצירת HTML לשינוי עם צבע
   */
  static formatChangeHTML(change: Change): string {
    const text = this.formatChangeText(change);
    const color = change.color || '#333';
    const fontWeight = change.type === 'new' ? 'bold' : 'normal';
    return `<span style="color: ${color}; font-weight: ${fontWeight};">${text}</span>`;
  }

  /**
   * יצירת HTML מפורט לשינוי (עבור תצוגות מפורטות יותר)
   */
  static formatDetailedChangeHTML(change: Change): string {
    switch (change.type) {
      case 'new':
        return `<div style="display: inline-block; background: ${change.color}; color: white; padding: 4px 8px; border-radius: 12px; font-size: 12px; font-weight: bold;">פוליסה חדשה</div>`;
      case 'handler':
        return `<div style="margin: 3px 0;"><strong style="color: #666;">יצרן:</strong> <span style="color: #666;">${change.from}</span> <span style="color: ${change.color}; font-weight: bold;">← ${change.to}</span></div>`;
      case 'fee':
        return `<div style="margin: 3px 0;"><strong style="color: #666;">דמי ניהול:</strong> <span style="color: #666;">${change.from}</span> <span style="color: ${change.color}; font-weight: bold;">← ${change.to}</span></div>`;
      case 'funds':
        return `<div style="margin: 3px 0; font-size: 13px;"><strong style="color: #666;">מסלול:</strong><br><span style="color: #999;">מקורי: ${change.from}</span><br><span style="color: ${change.color}; font-weight: bold;">חדש: ${change.to}</span></div>`;
      case 'new_recurrent_deposit':
        return `<div style="margin: 3px 0;"><strong style="color: #666;">הוראת קבע חדשה:</strong> <span style="color: ${change.color}; font-weight: bold;">${change.to}</span></div>`;
      case 'change_recurrent_deposit':
        return `<div style="margin: 3px 0;"><strong style="color: #666;">שינוי הוראת קבע:</strong> <span style="color: #666;">${change.from}</span> <span style="color: ${change.color}; font-weight: bold;">← ${change.to}</span></div>`;
      default:
        return this.formatChangeHTML(change);
    }
  }
}